/**
 * LicensorConsoleRelations — RelationsTab for LicensorConsole.
 * Extracted from LicensorConsole.jsx to reduce file complexity.
 */
import React, { useState, useEffect } from 'react';
import { Database } from 'lucide-react';
import { Section, SectionHeader, DataTable, FieldList, Skeleton, ErrorBanner } from './LicensorConsolePrimitives';
import { API, apiFetch } from '../lib/api';

export function RelationsTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    setLoading(true);
    apiFetch(`${API}/licensor/relations`).then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return <Skeleton label="Loading schema map..." />;
  if (error)   return <ErrorBanner error={error} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader title="Schema & Relationships" badge="HOW IT FITS TOGETHER" />

      <div className="alert-banner" style={{ borderColor: 'var(--erp-primary, #3b82f6)' }}>
        <Database size={16} color="var(--erp-primary, #3b82f6)" style={{ flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
            Primary key throughout the licensing platform: <code style={{ background:'var(--surface-3)', color:'var(--erp-primary, #3b82f6)', padding:'1px 6px', borderRadius:4 }}>{data.primaryKey}</code>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{data.description}</div>
        </div>
      </div>

      {data.secondaryKeys && (
        <Section title="Common keys — what bridges the tables" sub="These columns are how data from one table is joined / matched to another">
          <table className="data-table">
            <thead><tr><th>Key</th><th>Type</th><th>Scope</th><th>Appears in</th></tr></thead>
            <tbody>
              {data.secondaryKeys.map((k) => (
                <tr key={k.key || k.type}>
                  <td><code style={{ background:'#1e293b', color:'#60a5fa', padding:'2px 8px', borderRadius:4, fontSize:11, fontFamily:'monospace' }}>{k.key}</code></td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{k.type}</td>
                  <td style={{ fontSize: 12 }}>{k.scope}</td>
                  <td><div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{(k.appearsIn || []).map(a => <span key={a} className="badge badge-slate" style={{ fontFamily: 'monospace', fontSize: 12 }}>{a}</span>)}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      <Section title="Tables involved in licensing" sub="Each table's role + important columns">
        <div className="lc-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          {data.nodes.map((n) => (
            <div key={n.table || n.name || n.schema} className="card" style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span className="badge badge-blue" style={{ fontSize: 12 }}>{n.schema}</span>
                {n.primaryKey && <span style={{ fontSize: 12, color: '#94a3b8' }}>PK: {n.primaryKey}</span>}
              </div>
              <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 700, marginBottom: 4 }}>{n.role}</div>
              {n.foreignKey && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, fontFamily: 'monospace' }}>FK: {n.foreignKey}</div>}
              <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 4 }}>Important columns</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {n.importantColumns.map(c => <span key={c} className="badge badge-slate" style={{ fontFamily: 'monospace', fontSize: 12 }}>{c}</span>)}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Foreign-key relationships" sub="How tables connect (ClientId is the universal join)">
        <table className="data-table">
          <thead><tr><th>From</th><th>→ To</th><th>On column</th></tr></thead>
          <tbody>
            {data.relationships.map((rel) => (
              <tr key={`${rel.from}->${rel.to}`}>
                <td style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: '#3b82f6' }}>{rel.from}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{rel.to}</td>
                <td><span className="badge badge-slate" style={{ fontFamily: 'monospace' }}>{rel.on}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title={data.signupFlow.title} sub="What happens from signup to license issuance">
        <ol style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: '#334155', lineHeight: 1.7 }}>
          {data.signupFlow.steps.map((s, i) => <li key={`step-${i}`}>{s}</li>)}
        </ol>
      </Section>

      <div className="lc-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Section title="Mandatory signup fields (SaaS webhook)" sub="POST /api/saas/issue-license-on-signup">
          <FieldList label="Required"           items={data.signupFlow.mandatorySignupFields.forSaaSWebhook} required />
          <FieldList label="Recommended"        items={data.signupFlow.mandatorySignupFields.recommended} />
          <FieldList label="Defaulted if absent" items={data.signupFlow.mandatorySignupFields.defaultedIfAbsent} muted />
        </Section>
        <Section title="Manual license generation" sub={data.signupFlow.forManualLicenseGeneration.endpoint}>
          <FieldList label="Required" items={data.signupFlow.forManualLicenseGeneration.required} required />
          <FieldList label="Optional" items={data.signupFlow.forManualLicenseGeneration.optional} />
        </Section>
      </div>

      <Section title="User management during license creation" sub="What the +3 default users rule means">
        <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>
          Default users automatically added per license: <strong>{data.userManagementDuringLicenseCreation.defaultUsersAddedPerLicense}</strong>
        </div>
        <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#334155' }}>
          {data.userManagementDuringLicenseCreation.breakdown.map((s, i) => <li key={`bd-${i}`}>{s}</li>)}
        </ul>
        <div style={{ marginTop: 10, padding: 12, background: 'var(--surface-3)', borderRadius: 8, fontFamily: 'monospace', fontSize: 12, color: 'var(--erp-primary, #3b82f6)', border: '1px solid var(--border)' }}>
          {data.userManagementDuringLicenseCreation.formula}
        </div>
      </Section>
    </div>
  );
}
